import {PolymerElement, html} from '@polymer/polymer';
import '@polymer/polymer/lib/elements/dom-if.js';
import {ScopeBinding} from './behaviors/keli-binding-behavior.js';

class KeliUpload extends ScopeBinding(PolymerElement) {
  static get template() {
    return html`
<style>
  :host {
    display: block;
    width: 100%;
    box-sizing: border-box;
    padding: 20px;
    cursor: pointer;
    text-align: center;
    border: 2px dashed var(--keli-field-border-color);
    background-color: var(--keli-subtle-background);
  }

  :host(.dragging) {
    border-color: var(--keli-subtle-active-border);
    background-color: var(--keli-subtle-active-background);
  }

  h2 {
    margin: 0;
  }

  p {
    margin: 0;
  }

  #fileInput {
    display: none;
  }
</style>

<h2>Drop files to upload</h2>
<template is="dom-if" if="[[!isOverlay]]">
  <p>or click to browse a folder.</p>
</template>
<input id="fileInput" type="file" name="file[]" on-change="upload" multiple>`;
  }

  static get is() {
    return 'keli-upload';
  }

  static get properties() {
    return {
      keliControlTypes: {type: Array, value: ['upload']},
      dragging: {type: Boolean, observer: 'draggingChanged'},
      url: String,
      isOverlay: {type: Boolean, value: false},
      targetDefinition: {type: Object, scopevar: true, observer: 'targetDefinitionChanged'}
    }
  }

  constructor() {
    super();
    this.addEmitToKeli('attachmentUploadSuccess');
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.browseFiles);
    this.addEventListener('drop', this.handleDrop);
    this.addEventListener('dragenter', this.handleDragEnter);
    this.addEventListener('dragover', this.handleDragOver);
    this.addEventListener('dragleave', this.handleDragLeave);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.browseFiles);
    this.removeEventListener('drop', this.handleDrop);
    this.removeEventListener('dragenter', this.handleDragEnter);
    this.removeEventListener('dragover', this.handleDragOver);
    this.removeEventListener('dragleave', this.handleDragLeave);
  }

  browseFiles() {
    this.$.fileInput.click();
  }

  draggingChanged(newValue) {
    this.classList.toggle('dragging', newValue);
    if (!newValue) {
      this.dispatchEvent(new CustomEvent('uploadDraggingEnded', {
        bubbles: true,
        composed: true,
        detail: {element: this}
      }));
    }
  }

  targetDefinitionChanged(newValue) {
    let target = `${newValue.path}?scope=${encodeURIComponent(newValue.scope)}&row=${encodeURIComponent(newValue.row)}&context=${encodeURIComponent(newValue.context)}&user=${encodeURIComponent(newValue.user)}`;
    if (newValue.template) {
      target += `&template=${encodeURIComponent(newValue.template)}`;
    }
    if (newValue.field) {
      target += `&field=${encodeURIComponent(newValue.field)}`;
    }
    this.url = target;
  }

  handleDrop(event) {
    event.preventDefault();
    event.stopPropagation();
    this.dragging = false;
    this.upload(event);
  }

  handleDragEnter() {
    this.dragging = true;
  }

  handleDragOver(event) {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'copy';
    this.dragging = true;
  }

  handleDragLeave() {
    this.dragging = false;
  }

  upload(event) {
    const uploadFiles = (event.target && event.target.files) || (event.dataTransfer && event.dataTransfer.files);
    if (uploadFiles.length === 0) {
      this.$.fileInput.value = '';
      return;
    }
    if (!this.url) {
      this.$.fileInput.value = '';
      keliUILogger(new Error('No URL is specified to upload to.'));
      return;
    }
    const upload = this;
    const formData = new FormData();
    for (const file of uploadFiles) {
      formData.append(file.name, file);
    }

    const xhr = new XMLHttpRequest();
    xhr.open('POST', upload.url);
    xhr.upload.addEventListener('progress', event => {
      if (event.lengthComputable) {
        this.dispatchEvent(new CustomEvent('panelLoadingProgress', {
          bubbles: true,
          composed: true,
          detail: {progress: ((event.loaded / event.total) * 100)}
        }));
      }
    });
    xhr.upload.addEventListener('load', () => {
      this.dispatchEvent(new CustomEvent('panelLoadingProgress', {
        bubbles: true,
        composed: true,
        detail: {progress: 100}
      }));
    });

    xhr.onreadystatechange = () => {
      if (xhr.readyState === XMLHttpRequest.DONE && xhr.status === 200) {
        let {files} = JSON.parse(xhr.responseText);
        this.dispatchEvent(new CustomEvent('attachmentUploadSuccess', {detail: {files}}));
      }
    };

    xhr.send(formData);
    this.$.fileInput.value = '';
  }
}

customElements.define(KeliUpload.is, KeliUpload);
export {KeliUpload};
